/**
 * AI Intake Lab — Phase 1, Slice 4A/4B/4C (V-OCIS-Intake-4*).
 *
 * Internal-only page for exercising the OCIS AI Intake backend
 * end-to-end with manual text:
 *
 *   intake-create  →  extract-intake  →  approve-intake
 *
 * Post-approval handoff (Slice 4B + 4C):
 *   - choose the ticket's jobs BEFORE approval (PR #13 I-3): approve-intake creates the ticket with the
 *     advisor-selected jobs in one transaction (FD-2026-10-24-C); `intake-attach-jobs` remains only for drafts
 *     approved before that change
 *   - hand off to OptiPlaceModal for technician assignment (existing
 *     reassign-mode path — see OptiPlaceModal.tsx:1515)
 *
 * Both post-approval actions are human-triggered; no auto-attach, no
 * auto-assign. approve-intake remains a pure ticket-create operation.
 *
 * Not the final production intake UX. This page exists so internal
 * admins can validate the deployed edge functions, prompt quality,
 * and the approval-to-ticket loop without audio.
 *
 * Gating (defense-in-depth):
 *   - Route is wrapped in <InternalAdminRoute> in App.tsx (403 for
 *     non-internal-admins).
 *   - Component additionally checks useFeatureFlag('aiIntakeEnabled')
 *     and renders a placeholder if false (per-shop opt-in).
 *
 * Out of scope for this slice:
 *   - Audio recording / upload.
 *   - Live transcription.
 *   - Catalog-mapped suggested jobs (Gemini still returns freeform
 *     `name` — the `code` field on ExtractedSuggestedJob is forward-
 *     compat for the catalog-mapping work, currently never populated).
 *   - TopBar / nav surfacing.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { toast } from '@/hooks/use-toast';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { OptiPlaceModal } from '@/components/tickets/OptiPlaceModal';

// ===================================================================
// Backend response types (mirror server contracts)
// ===================================================================

interface IntakeCreateResponse {
  ok: true;
  transcript_id: string;
}

interface ExtractedConcern {
  concern_text?: string;
  symptoms?: string[];
  when_it_happens?: string | null;
}

interface ExtractedRequestedService {
  name?: string;
  category?: string | null;
  urgency?: string | null;
}

interface ExtractedSuggestedJob {
  name?: string;
  category?: string | null;
  estimated_minutes?: number | null;
  skill_level?: string | null;
  rationale?: string;
  // V-OCIS-Intake-Catalog-Batch-v1: forward-compat catalog-mapping
  // fields. extract-intake does NOT populate these yet — Gemini still
  // returns freeform suggestions. intake-attach-jobs uses `code` as
  // the Tier-1 catalog match key when present (with label / alias as
  // fallbacks). Once extract-intake gains candidate retrieval, it
  // will populate `code` plus the four metadata fields for UI display
  // (badge tooltips, "no match" warnings, etc.).
  code?: string | null;
  catalog_label?: string | null;
  category_key?: string | null;
  sub_menu?: string | null;
  match_confidence?: 'high' | 'medium' | 'low' | 'none' | string | null;
  match_method?: string | null;
}

// =====================================================================
// intake-attach-jobs request/response shapes (mirror server contracts)
// =====================================================================

interface IntakeAttachJobsSkippedEntry {
  name: string;
  reason: 'no_catalog_match' | 'ambiguous_catalog_match' | 'retired_catalog_job' | 'job_disabled_for_shop' | 'duplicate_in_ticket';
}

interface IntakeAttachJobsResponse {
  ok: true;
  ticket_id: string;
  attached_count: number;
  /**
   * Idempotency short-circuit: when ticket_jobs.notes already carries
   * the `[ai_intake:<draft_id>]` marker, the server returns
   * `already_attached: true` with the prior attached_count and omits
   * attached_job_ids + skipped. Treat as a no-op success.
   */
  already_attached?: boolean;
  attached_job_ids?: string[];
  skipped?: IntakeAttachJobsSkippedEntry[];
}

interface ExtractedIntake {
  channel?: string;
  is_waiter?: boolean | null;
  is_drop_off?: boolean | null;
  promised_time?: string | null;
  urgency?: string | null;
  advisor_notes?: string | null;
}

interface Extraction {
  schema_version: string;
  confidence: number;
  summary: string;
  customer: {
    full_name: string | null;
    phone: string | null;
    email: string | null;
    is_existing_match_hint?: string | null;
  };
  vehicle: {
    year: number | null;
    make: string | null;
    model: string | null;
    trim?: string | null;
    vin: string | null;
    license_plate: string | null;
    mileage: number | null;
  };
  intake: ExtractedIntake;
  concerns: ExtractedConcern[];
  requested_services: ExtractedRequestedService[];
  suggested_jobs: ExtractedSuggestedJob[];
  risk_flags: string[];
  missing_info: string[];
}

interface ExtractIntakeResponse {
  ok: true;
  intake_draft_id: string;
  extraction: Extraction;
}

interface ApproveIntakeResponse {
  ok: true;
  intake_draft_id: string;
  ticket_id: string;
  customer_id?: string;
  vehicle_id?: string;
  customer_action?: 'matched' | 'created';
  vehicle_action?: 'matched' | 'created';
  already_approved?: boolean;
  /** Core-100 R4 W4 (CORE-BA): the conversation's inbox thread attach outcome; 'failed' is shown, never swallowed. */
  inbox_attach?: 'none' | 'attached' | 'already_attached' | 'failed';
  /** PR #13 I-2: jobs created with the ticket by this request (0 for a replay of an earlier approval). */
  jobs_attached?: number;
}

/** PR #13 I-2: approve-intake's refusal when a selected job does not resolve to an available catalog job. */
interface UnresolvedJob {
  name: string;
  reason: IntakeAttachJobsSkippedEntry['reason'];
}

/** Plain-language reasons for an unresolved selection (the advisor changes or removes the job). */
const UNRESOLVED_REASON_TEXT: Record<UnresolvedJob['reason'], string> = {
  no_catalog_match: 'no catalog job has this name',
  ambiguous_catalog_match: 'more than one catalog job matches this name',
  retired_catalog_job: 'this catalog job is retired',
  job_disabled_for_shop: 'this job is turned off for your shop',
  duplicate_in_ticket: 'already on the ticket',
};

/** A failed edge call, with the function's JSON body when it sent one (e.g. code + unresolved). */
class InvokeError extends Error {
  constructor(message: string, readonly body: Record<string, unknown> | null) {
    super(message);
  }
}

// ===================================================================
// Editable form state shape (subset of reviewed_extraction)
// ===================================================================

interface CustomerForm {
  full_name: string;
  phone: string;
  email: string;
}

interface VehicleForm {
  year: string;
  make: string;
  model: string;
  vin: string;
  license_plate: string;
  mileage: string;
}

interface IntakeForm {
  promised_time: string; // datetime-local input value
  advisor_notes: string;
}

type Step = 'paste' | 'transcribed' | 'review' | 'done';

// ===================================================================
// Helpers
// ===================================================================

/**
 * Invoke a Supabase edge function with the canonical 3-layer error
 * handling pattern (mirrors src/hooks/tickets/useTicketCustody.ts):
 *  1. supabase-js network/auth error
 *  2. empty data response
 *  3. body `ok: false` shape
 *
 * Throws an Error with the best available message on any failure;
 * console.errors useful diagnostic context for dev testing.
 */
async function invokeFn<T extends { ok: true }>(
  name: string,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    // A non-2xx answer: its JSON body is in data or, with supabase-js, on error.context (a Response).
    let errBody = (data as Record<string, unknown> | null) ?? null;
    if (!errBody) {
      try {
        errBody = await (error as { context?: { json?: () => Promise<Record<string, unknown>> } }).context?.json?.() ?? null;
      } catch {
        errBody = null;
      }
    }
    const message =
      (errBody as { error?: string; message?: string } | null)?.error ||
      (errBody as { error?: string; message?: string } | null)?.message ||
      error.message ||
      `${name} failed (network/auth error)`;

    console.error(`[ai-intake-lab] ${name} error`, error, errBody);
    throw new InvokeError(message, errBody);
  }
  if (!data) {
     
    console.error(`[ai-intake-lab] ${name} returned empty data — is the function deployed?`);
    throw new Error(`${name} returned no data`);
  }
  const payload = data as { ok?: boolean; error?: string };
  if (payload.ok === false) {

    console.error(`[ai-intake-lab] ${name} returned ok:false`, data);
    throw new InvokeError(payload.error ?? `${name} returned ok:false`, data as Record<string, unknown>);
  }
  return data as T;
}

/** The suggestions that can be sent (a non-empty name); the default selection. */
function namedJobIndices(extraction: Extraction): Set<number> {
  const out = new Set<number>();
  extraction.suggested_jobs.forEach((job, idx) => {
    if ((job.name ?? '').trim().length > 0) out.add(idx);
  });
  return out;
}

/**
 * Convert a `<input type="datetime-local">` string (YYYY-MM-DDTHH:MM)
 * into an ISO-8601 timestamp with timezone, or null if empty. Approve-
 * intake stores this as `scheduled_start_time` (timestamptz).
 */
function datetimeLocalToIso(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

/**
 * Coerce an editable text-input to either a number or null. Used for
 * vehicle.year / vehicle.mileage which are typed `number | null` on
 * the wire.
 */
function maybeNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/**
 * Sanitize a string field from the AI extraction for use as a
 * React input value. Handles three sources of literal `"null"`:
 *  1. Actual JSON null / undefined (most common).
 *  2. Gemini function-calling sometimes emits the LITERAL string
 *     "null" instead of the JSON null primitive — `?? ''` doesn't
 *     catch that, so we need an explicit string-equality check.
 *  3. Whitespace-only strings (advisor expects "empty" UX).
 */
function sanitizeText(v: string | null | undefined): string {
  if (v == null) return '';
  const trimmed = String(v).trim();
  if (trimmed === '') return '';
  const lower = trimmed.toLowerCase();
  if (lower === 'null' || lower === 'undefined') return '';
  return trimmed;
}

/**
 * Same Gemini-string-"null" defense for numeric fields. Returns
 * the empty string when the value should NOT be rendered, or the
 * canonical numeric string otherwise.
 */
function sanitizeNumberText(v: number | null | undefined): string {
  if (v == null) return '';
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  // Defensive: AI sometimes emits string-encoded numbers.
  const trimmed = String(v).trim();
  if (trimmed === '') return '';
  const lower = trimmed.toLowerCase();
  if (lower === 'null' || lower === 'undefined') return '';
  const n = Number(trimmed);
  return Number.isFinite(n) ? String(n) : '';
}

function emptyFromExtraction(ex: Extraction): {
  customer: CustomerForm;
  vehicle: VehicleForm;
  intake: IntakeForm;
} {
  return {
    customer: {
      full_name: sanitizeText(ex.customer?.full_name),
      phone: sanitizeText(ex.customer?.phone),
      email: sanitizeText(ex.customer?.email),
    },
    vehicle: {
      year: sanitizeNumberText(ex.vehicle?.year),
      make: sanitizeText(ex.vehicle?.make),
      model: sanitizeText(ex.vehicle?.model),
      vin: sanitizeText(ex.vehicle?.vin),
      license_plate: sanitizeText(ex.vehicle?.license_plate),
      mileage: sanitizeNumberText(ex.vehicle?.mileage),
    },
    intake: {
      // promised_time arrives as ISO; the input expects local-datetime
      // shape. We leave it as the ISO string — datetime-local is
      // tolerant enough to display it. Approve-intake re-normalises
      // back to ISO on submit.
      promised_time: sanitizeText(ex.intake?.promised_time),
      advisor_notes: sanitizeText(ex.intake?.advisor_notes),
    },
  };
}

function confidenceBadgeClass(confidence: number): string {
  if (confidence >= 0.8) return 'bg-emerald-600 hover:bg-emerald-600';
  if (confidence >= 0.5) return 'bg-amber-600 hover:bg-amber-600';
  return 'bg-red-600 hover:bg-red-600';
}

// ===================================================================
// Component
// ===================================================================

export default function AiIntakeLab() {
  const navigate = useNavigate();
  const { activeShop } = useAuth();
  const shopId = activeShop?.id ?? null;
  const aiIntakeEnabled = useFeatureFlag('aiIntakeEnabled');

  const [step, setStep] = useState<Step>('paste');

  // Step 1
  const [transcriptText, setTranscriptText] = useState('');
  const [transcriptId, setTranscriptId] = useState<string | null>(null);

  // Step 2
  const [extraction, setExtraction] = useState<Extraction | null>(null);
  const [intakeDraftId, setIntakeDraftId] = useState<string | null>(null);

  // Step 3 editable form
  const [customerForm, setCustomerForm] = useState<CustomerForm>({
    full_name: '', phone: '', email: '',
  });
  const [vehicleForm, setVehicleForm] = useState<VehicleForm>({
    year: '', make: '', model: '', vin: '', license_plate: '', mileage: '',
  });
  const [intakeForm, setIntakeForm] = useState<IntakeForm>({
    promised_time: '', advisor_notes: '',
  });

  // Step 4
  const [approveResult, setApproveResult] = useState<ApproveIntakeResponse | null>(null);

  // The ticket's jobs, chosen in the review step BEFORE approval (PR #13 I-3; FD-2026-10-24-C), and — only for a
  // draft approved before that change — attached afterwards in Step 4B.
  // selectedJobIndices keys against the extraction.suggested_jobs array.
  // Default-pre-select happens once when the done step renders; subsequent
  // clicks toggle membership. attachResult holds the server reply (or
  // synthetic error) so the UI can render attached/skipped/already-attached.
  const [selectedJobIndices, setSelectedJobIndices] = useState<Set<number>>(new Set());
  const [attaching, setAttaching] = useState(false);
  const [attachResult, setAttachResult] = useState<IntakeAttachJobsResponse | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  // PR #13 I-3: the selection approve-intake was sent, and its refusal of unresolved selections (review stays open).
  const [approvedJobNames, setApprovedJobNames] = useState<string[]>([]);
  const [unresolvedJobs, setUnresolvedJobs] = useState<UnresolvedJob[] | null>(null);

  // Step 4C — OptiAssign handoff (post-approval, post-attach)
  const [isOptiPlaceOpen, setIsOptiPlaceOpen] = useState(false);

  // Loading flags per step
  const [creating, setCreating] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [approving, setApproving] = useState(false);

  // ---------------------------------------------------------------
  // Flag / shop gates
  // ---------------------------------------------------------------
  if (!aiIntakeEnabled) {
    return (
      <div className="container mx-auto py-12 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle>AI Intake Lab</CardTitle>
            <CardDescription>
              Internal-only workbench for the OCIS AI Intake pipeline.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertTitle>Feature not enabled</AlertTitle>
              <AlertDescription>
                AI Intake Lab is not enabled for this shop. An internal admin
                must turn on the <code>aiIntakeEnabled</code> feature flag from
                <code className="ml-1">/feature-flags</code>.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!shopId) {
    return (
      <div className="container mx-auto py-12 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle>AI Intake Lab</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertTitle>No shop selected</AlertTitle>
              <AlertDescription>
                Select an active shop before using the AI Intake Lab.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------------------------------------------------------------
  // Step actions
  // ---------------------------------------------------------------

  async function handleCreateTranscript() {
    const text = transcriptText.trim();
    if (!text) {
      toast({
        title: 'Transcript text required',
        description: 'Paste or type the intake conversation first.',
        variant: 'destructive',
      });
      return;
    }
    setCreating(true);
    try {
      const result = await invokeFn<IntakeCreateResponse>('intake-create', {
        shop_id: shopId,
        transcript_text: text,
      });
      setTranscriptId(result.transcript_id);
      setStep('transcribed');
      toast({
        title: 'Transcript created',
        description: `transcript_id: ${result.transcript_id}`,
      });
    } catch (err) {
      toast({
        title: 'intake-create failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  }

  async function handleExtract() {
    if (!transcriptId) return;
    setExtracting(true);
    try {
      const result = await invokeFn<ExtractIntakeResponse>('extract-intake', {
        shop_id: shopId,
        transcript_id: transcriptId,
      });
      setIntakeDraftId(result.intake_draft_id);
      setExtraction(result.extraction);

      const prefilled = emptyFromExtraction(result.extraction);
      setCustomerForm(prefilled.customer);
      setVehicleForm(prefilled.vehicle);
      setIntakeForm(prefilled.intake);
      // PR #13 I-3: pre-select every named suggestion for the ticket; the advisor reviews the selection before
      // approval. A suggestion without a name cannot be sent, so it cannot be selected.
      setSelectedJobIndices(namedJobIndices(result.extraction));
      setUnresolvedJobs(null);

      setStep('review');
      toast({
        title: 'Extraction ready',
        description: `intake_draft_id: ${result.intake_draft_id}`,
      });
    } catch (err) {
      toast({
        title: 'extract-intake failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setExtracting(false);
    }
  }

  async function handleApprove() {
    if (!intakeDraftId) return;
    const fullName = customerForm.full_name.trim();
    if (!fullName) {
      toast({
        title: 'Customer name required',
        description: 'reviewed_extraction.customer.full_name must be non-empty.',
        variant: 'destructive',
      });
      return;
    }
    const year = maybeNumber(vehicleForm.year);
    const make = vehicleForm.make.trim();
    const model = vehicleForm.model.trim();
    if (year == null || !make || !model) {
      toast({
        title: 'Vehicle fields required',
        description: 'reviewed_extraction.vehicle.year / make / model must be set.',
        variant: 'destructive',
      });
      return;
    }
    const selectedJobs = selectedJobsForRequest();
    if (selectedJobs.length === 0) {
      toast({
        title: 'Select at least one job',
        description: 'A ticket needs at least one job. Select the work for this ticket, then approve.',
        variant: 'destructive',
      });
      return;
    }
    setApproving(true);
    setUnresolvedJobs(null);
    try {
      const result = await invokeFn<ApproveIntakeResponse>('approve-intake', {
        shop_id: shopId,
        intake_draft_id: intakeDraftId,
        reviewed_extraction: {
          customer: {
            full_name: fullName,
            phone: customerForm.phone.trim() || null,
            email: customerForm.email.trim() || null,
          },
          vehicle: {
            year,
            make,
            model,
            vin: vehicleForm.vin.trim() || null,
            license_plate: vehicleForm.license_plate.trim() || null,
            mileage: maybeNumber(vehicleForm.mileage),
          },
          intake: {
            promised_time: datetimeLocalToIso(intakeForm.promised_time),
            advisor_notes: intakeForm.advisor_notes.trim() || null,
          },
        },
        // PR #13 I-2 contract: the ticket is created with exactly these jobs, in one transaction.
        selected_jobs: selectedJobs,
      });
      setApproveResult(result);
      setApprovedJobNames(result.already_approved ? [] : selectedJobs.map((j) => j.name));
      setStep('done');
      // Step 4B (a draft approved before PR #13 I-2 only): start from the named suggestions.
      if (extraction) setSelectedJobIndices(namedJobIndices(extraction));
      setAttachResult(null);
      setAttachError(null);
      toast({
        title: result.already_approved ? 'Already approved' : 'Ticket created',
        description: `ticket_id: ${result.ticket_id}`,
      });
      if (result.inbox_attach === 'failed') {
        toast({
          title: 'Conversation not attached',
          description: 'The ticket was created, but its inbox conversation could not be attached to it. It is still in the inbox.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      const body = err instanceof InvokeError ? err.body : null;
      if (body?.code === 'JOB_SELECTION_UNRESOLVED' && Array.isArray(body.unresolved)) {
        // Stay in review with the selection intact; the advisor changes or removes the listed jobs.
        setUnresolvedJobs(body.unresolved as UnresolvedJob[]);
      }
      toast({
        title: 'approve-intake failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setApproving(false);
    }
  }

  function resetForAnotherIntake() {
    setStep('paste');
    setTranscriptText('');
    setTranscriptId(null);
    setExtraction(null);
    setIntakeDraftId(null);
    setCustomerForm({ full_name: '', phone: '', email: '' });
    setVehicleForm({
      year: '', make: '', model: '', vin: '', license_plate: '', mileage: '',
    });
    setIntakeForm({ promised_time: '', advisor_notes: '' });
    setApproveResult(null);
    // V-OCIS-Intake-4B/4C reset
    setSelectedJobIndices(new Set());
    setAttaching(false);
    setAttachResult(null);
    setAttachError(null);
    setApprovedJobNames([]);
    setUnresolvedJobs(null);
    setIsOptiPlaceOpen(false);
  }

  /** The advisor's selection in the approve-intake / intake-attach-jobs shape (named suggestions only). */
  function selectedJobsForRequest() {
    if (!extraction) return [];
    return extraction.suggested_jobs
      .map((job, idx) => ({ job, idx }))
      .filter(({ idx }) => selectedJobIndices.has(idx))
      .map(({ job }) => ({
        name: (job.name ?? '').trim(),
        code: job.code ?? null,
        rationale: job.rationale ?? null,
        estimated_minutes: job.estimated_minutes ?? null,
      }))
      .filter((j) => j.name.length > 0);
  }

  function handleGoToOptiboard() {
    if (!approveResult?.ticket_id) return;
    navigate(`/?tab=dayboard&highlight=${encodeURIComponent(approveResult.ticket_id)}`);
  }

  /**
   * V-OCIS-Intake-4B: attach the advisor-selected subset of the
   * extraction's suggested_jobs to the created ticket. Human-triggered
   * only; no auto-attach. Sends only `name` + optional `code` /
   * `rationale` / `estimated_minutes` — backward-compatible with the
   * pre-catalog-mapping server contract.
   */
  async function handleAttachJobs() {
    if (!approveResult?.ticket_id || !intakeDraftId || !extraction) return;
    if (selectedJobIndices.size === 0) return;

    const selectedJobs = extraction.suggested_jobs
      .map((job, idx) => ({ job, idx }))
      .filter(({ idx }) => selectedJobIndices.has(idx))
      .map(({ job }) => ({
        name: (job.name ?? '').trim(),
        code: job.code ?? null,
        rationale: job.rationale ?? null,
        estimated_minutes: job.estimated_minutes ?? null,
      }))
      .filter((j) => j.name.length > 0);

    if (selectedJobs.length === 0) {
      toast({
        title: 'No selectable jobs',
        description: 'Selected jobs have no name to send.',
        variant: 'destructive',
      });
      return;
    }

    setAttaching(true);
    setAttachError(null);
    try {
      const result = await invokeFn<IntakeAttachJobsResponse>('intake-attach-jobs', {
        shop_id: shopId,
        intake_draft_id: intakeDraftId,
        ticket_id: approveResult.ticket_id,
        jobs: selectedJobs,
      });
      setAttachResult(result);
      toast({
        // attach uses `already_attached` (idempotency short-circuit); the
        // approve response is the one that carries `already_approved`.
        title: result.already_attached
          ? 'Already attached'
          : result.already_attached
            ? 'Jobs already attached'
            : `Attached ${result.attached_count} job${result.attached_count === 1 ? '' : 's'}`,
        description: result.skipped && result.skipped.length > 0
          ? `${result.skipped.length} skipped — see card below.`
          : undefined,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setAttachError(msg);
      toast({
        title: 'intake-attach-jobs failed',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setAttaching(false);
    }
  }

  function toggleJobSelected(idx: number) {
    setSelectedJobIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------

  return (
    <div className="container mx-auto py-8 max-w-5xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold">AI Intake Lab</h1>
        <p className="text-muted-foreground">
          Internal workbench for the OCIS AI Intake pipeline (intake-create →
          extract-intake → approve-intake). Validation only — not the final
          intake UX.
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          Active shop: <code>{shopId}</code> · Step: <code>{step}</code>
        </p>
      </div>

      {/* ---------- Step 1: paste ---------- */}
      {(step === 'paste' || step === 'transcribed') && (
        <Card>
          <CardHeader>
            <CardTitle>1. Paste intake conversation</CardTitle>
            <CardDescription>
              Type or paste the advisor/customer dialogue. PII is intentionally
              preserved end-to-end for the audited intake-extraction bypass.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={transcriptText}
              onChange={(e) => setTranscriptText(e.target.value)}
              placeholder="Customer: Hi, my 2019 Civic is making a rattling noise above 40 mph..."
              rows={10}
              disabled={step !== 'paste' || creating}
            />
            <div className="flex items-center gap-3">
              <Button
                onClick={handleCreateTranscript}
                disabled={creating || step !== 'paste'}
              >
                {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Intake Transcript
              </Button>
              {transcriptId && (
                <span className="text-sm text-muted-foreground">
                  transcript_id: <code>{transcriptId}</code>
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---------- Step 2: extract ---------- */}
      {step === 'transcribed' && (
        <Card>
          <CardHeader>
            <CardTitle>2. Extract structured intake</CardTitle>
            <CardDescription>
              Calls <code>extract-intake</code>, which routes through the
              audited PII-bypass and asks Gemini for a structured draft.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <Button onClick={handleExtract} disabled={extracting}>
                {extracting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Extract with AI
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setTranscriptId(null);
                  setStep('paste');
                }}
                disabled={extracting}
              >
                Back to paste
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---------- Step 3: review / approve ---------- */}
      {step === 'review' && extraction && (
        <>
          {/* Editable approval form */}
          <Card>
            <CardHeader>
              <CardTitle>3. Review &amp; approve</CardTitle>
              <CardDescription>
                Edit the AI-extracted fields as needed, then approve to create a
                real ticket via the canonical OptiBay mutation path.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Customer */}
              <section>
                <h3 className="font-semibold mb-2">Customer</h3>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-1">
                    <Label htmlFor="cust-name">Full name *</Label>
                    <Input
                      id="cust-name"
                      value={customerForm.full_name}
                      onChange={(e) =>
                        setCustomerForm((f) => ({ ...f, full_name: e.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="cust-phone">Phone</Label>
                    <Input
                      id="cust-phone"
                      value={customerForm.phone}
                      onChange={(e) =>
                        setCustomerForm((f) => ({ ...f, phone: e.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="cust-email">Email</Label>
                    <Input
                      id="cust-email"
                      type="email"
                      value={customerForm.email}
                      onChange={(e) =>
                        setCustomerForm((f) => ({ ...f, email: e.target.value }))
                      }
                    />
                  </div>
                </div>
              </section>

              {/* Vehicle */}
              <section>
                <h3 className="font-semibold mb-2">Vehicle</h3>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-1">
                    <Label htmlFor="veh-year">Year *</Label>
                    <Input
                      id="veh-year"
                      type="number"
                      value={vehicleForm.year}
                      onChange={(e) =>
                        setVehicleForm((f) => ({ ...f, year: e.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="veh-make">Make *</Label>
                    <Input
                      id="veh-make"
                      value={vehicleForm.make}
                      onChange={(e) =>
                        setVehicleForm((f) => ({ ...f, make: e.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="veh-model">Model *</Label>
                    <Input
                      id="veh-model"
                      value={vehicleForm.model}
                      onChange={(e) =>
                        setVehicleForm((f) => ({ ...f, model: e.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="veh-vin">VIN</Label>
                    <Input
                      id="veh-vin"
                      value={vehicleForm.vin}
                      onChange={(e) =>
                        setVehicleForm((f) => ({ ...f, vin: e.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="veh-plate">License plate</Label>
                    <Input
                      id="veh-plate"
                      value={vehicleForm.license_plate}
                      onChange={(e) =>
                        setVehicleForm((f) => ({ ...f, license_plate: e.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="veh-mileage">Mileage</Label>
                    <Input
                      id="veh-mileage"
                      type="number"
                      value={vehicleForm.mileage}
                      onChange={(e) =>
                        setVehicleForm((f) => ({ ...f, mileage: e.target.value }))
                      }
                    />
                  </div>
                </div>
              </section>

              {/* Intake */}
              <section>
                <h3 className="font-semibold mb-2">Intake</h3>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label htmlFor="intake-promised">Promised time</Label>
                    <Input
                      id="intake-promised"
                      type="datetime-local"
                      value={intakeForm.promised_time}
                      onChange={(e) =>
                        setIntakeForm((f) => ({ ...f, promised_time: e.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <Label htmlFor="intake-notes">Advisor notes</Label>
                    <Textarea
                      id="intake-notes"
                      value={intakeForm.advisor_notes}
                      onChange={(e) =>
                        setIntakeForm((f) => ({ ...f, advisor_notes: e.target.value }))
                      }
                      rows={3}
                    />
                  </div>
                </div>
              </section>

              <section data-testid="intake-ticket-jobs">
                <h3 className="font-semibold mb-1">Jobs for this ticket</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  The ticket is created with the jobs you select here. Each one must match a catalog job.
                </p>
                {extraction && extraction.suggested_jobs.length > 0 ? (
                  <ul className="space-y-2">
                    {extraction.suggested_jobs.map((j, i) => {
                      const hasName = (j.name ?? '').trim().length > 0;
                      const checkboxId = `intake-review-job-${i}`;
                      return (
                        <li key={i} className="flex items-start gap-3 rounded border p-2">
                          <Checkbox
                            id={checkboxId}
                            checked={selectedJobIndices.has(i)}
                            onCheckedChange={() => toggleJobSelected(i)}
                            disabled={approving || !hasName}
                            aria-label={`Select ${j.name || '(unnamed job)'} for this ticket`}
                          />
                          <label htmlFor={checkboxId} className="flex-1 cursor-pointer text-sm">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{hasName ? j.name : <em>(unnamed)</em>}</span>
                              {j.estimated_minutes != null && <Badge variant="outline">{j.estimated_minutes} min</Badge>}
                              {j.category && <Badge variant="secondary">{j.category}</Badge>}
                              {!hasName && <Badge variant="destructive">no name — can't be added</Badge>}
                            </div>
                            {j.rationale && <div className="text-xs text-muted-foreground mt-1">{j.rationale}</div>}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground"><em>No suggested jobs.</em></p>
                )}
                {selectedJobsForRequest().length === 0 && (
                  <p className="text-sm text-destructive mt-2" role="status">
                    Select at least one job to approve. A ticket needs at least one job.
                  </p>
                )}
                {unresolvedJobs && unresolvedJobs.length > 0 && (
                  <Alert variant="destructive" className="mt-2">
                    <AlertTitle>Some selected jobs could not be matched</AlertTitle>
                    <AlertDescription>
                      <p>No ticket was created. Change or remove these jobs, then approve again:</p>
                      <ul className="list-disc pl-4 text-xs mt-1">
                        {unresolvedJobs.map((u, idx) => (
                          <li key={idx}>
                            <span className="font-medium">{u.name}</span>
                            {' — '}
                            {UNRESOLVED_REASON_TEXT[u.reason] ?? u.reason}
                          </li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </section>

              <div className="flex items-center gap-3">
                <Button onClick={handleApprove} disabled={approving || selectedJobsForRequest().length === 0}>
                  {approving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Approve &amp; Create Ticket
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStep('transcribed')}
                  disabled={approving}
                >
                  Back to extract
                </Button>
                <span className="text-sm text-muted-foreground">
                  intake_draft_id: <code>{intakeDraftId}</code>
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Read-only extraction context */}
          <Card>
            <CardHeader>
              <CardTitle>Extraction context (read-only)</CardTitle>
              <CardDescription>
                Display only. Suggested jobs are not attached to the ticket in
                this slice (deferred to Slice 3b).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge className={confidenceBadgeClass(extraction.confidence)}>
                  Confidence: {extraction.confidence.toFixed(2)}
                </Badge>
                {extraction.schema_version && (
                  <Badge variant="outline">schema {extraction.schema_version}</Badge>
                )}
              </div>

              <section>
                <h4 className="font-semibold text-sm mb-1">Summary</h4>
                <p className="text-sm text-muted-foreground">
                  {extraction.summary || <em>(none)</em>}
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-sm mb-1">Concerns</h4>
                {extraction.concerns.length === 0 ? (
                  <p className="text-sm text-muted-foreground"><em>(none)</em></p>
                ) : (
                  <ul className="space-y-2">
                    {extraction.concerns.map((c, i) => (
                      <li key={i} className="text-sm">
                        <div className="font-medium">{c.concern_text}</div>
                        {c.when_it_happens && (
                          <div className="text-xs text-muted-foreground">
                            When: {c.when_it_happens}
                          </div>
                        )}
                        {c.symptoms && c.symptoms.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {c.symptoms.map((s, j) => (
                              <Badge key={j} variant="secondary">{s}</Badge>
                            ))}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h4 className="font-semibold text-sm mb-1">Requested services</h4>
                {extraction.requested_services.length === 0 ? (
                  <p className="text-sm text-muted-foreground"><em>(none)</em></p>
                ) : (
                  <ul className="text-sm list-disc pl-5">
                    {extraction.requested_services.map((s, i) => (
                      <li key={i}>
                        {s.name}
                        {s.category && <span className="text-muted-foreground"> · {s.category}</span>}
                        {s.urgency && <span className="text-muted-foreground"> · {s.urgency}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h4 className="font-semibold text-sm mb-1">Suggested jobs (display-only)</h4>
                {extraction.suggested_jobs.length === 0 ? (
                  <p className="text-sm text-muted-foreground"><em>(none)</em></p>
                ) : (
                  <ul className="space-y-2">
                    {extraction.suggested_jobs.map((j, i) => (
                      <li key={i} className="text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{j.name}</span>
                          {j.skill_level && (
                            <Badge variant="outline">{j.skill_level}</Badge>
                          )}
                          {j.estimated_minutes != null && (
                            <Badge variant="outline">{j.estimated_minutes} min</Badge>
                          )}
                          {j.category && (
                            <Badge variant="secondary">{j.category}</Badge>
                          )}
                        </div>
                        {j.rationale && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {j.rationale}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="grid gap-3 md:grid-cols-2">
                <div>
                  <h4 className="font-semibold text-sm mb-1">Missing info</h4>
                  {extraction.missing_info.length === 0 ? (
                    <p className="text-sm text-muted-foreground"><em>(none)</em></p>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {extraction.missing_info.map((m, i) => (
                        <Badge key={i} variant="outline">{m}</Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="font-semibold text-sm mb-1">Risk flags</h4>
                  {extraction.risk_flags.length === 0 ? (
                    <p className="text-sm text-muted-foreground"><em>(none)</em></p>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {extraction.risk_flags.map((r, i) => (
                        <Badge key={i} variant="destructive">{r}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            </CardContent>
          </Card>
        </>
      )}

      {/* ---------- Step 4: done ---------- */}
      {step === 'done' && approveResult && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>
                {approveResult.already_approved ? 'Already approved' : 'Ticket created'}
              </CardTitle>
              <CardDescription>
                The intake draft has been promoted to a real operational ticket.
                Recommended next step: assign with OptiAssign.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-muted-foreground">ticket_id</dt>
                <dd><code>{approveResult.ticket_id}</code></dd>
                <dt className="text-muted-foreground">intake_draft_id</dt>
                <dd><code>{approveResult.intake_draft_id}</code></dd>
                {approveResult.customer_id && (
                  <>
                    <dt className="text-muted-foreground">customer_id</dt>
                    <dd>
                      <code>{approveResult.customer_id}</code>
                      {approveResult.customer_action && (
                        <Badge className="ml-2" variant="outline">
                          {approveResult.customer_action}
                        </Badge>
                      )}
                    </dd>
                  </>
                )}
                {approveResult.vehicle_id && (
                  <>
                    <dt className="text-muted-foreground">vehicle_id</dt>
                    <dd>
                      <code>{approveResult.vehicle_id}</code>
                      {approveResult.vehicle_action && (
                        <Badge className="ml-2" variant="outline">
                          {approveResult.vehicle_action}
                        </Badge>
                      )}
                    </dd>
                  </>
                )}
              </dl>
              {approvedJobNames.length > 0 && (
                <div data-testid="intake-jobs-created">
                  <p className="text-sm font-medium">
                    Jobs added with the ticket ({approveResult.jobs_attached ?? approvedJobNames.length})
                  </p>
                  <ul className="list-disc pl-5 text-sm">
                    {approvedJobNames.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                  <p className="text-xs text-muted-foreground mt-1">
                    Suggestions you did not select were not added. Add further work from the ticket card.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ---------- Step 4B: attach suggested jobs — ONLY for a draft approved before PR #13 I-2 ---------- */}
          {/* A new approval already created the ticket with its jobs; offering attach again would duplicate work. */}
          {approveResult.already_approved && extraction && extraction.suggested_jobs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Attach suggested jobs</CardTitle>
                <CardDescription>
                  This draft was approved earlier. If its ticket has no intake jobs yet, attach them here.
                  Select which AI-suggested jobs to attach to the ticket.
                  Server matches each by catalog label and skips unmatched or
                  ambiguous entries — see results below.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2">
                  {extraction.suggested_jobs.map((j, i) => {
                    const hasName = (j.name ?? '').trim().length > 0;
                    const isChecked = selectedJobIndices.has(i);
                    const disabled = attaching || !hasName || !!attachResult;
                    const checkboxId = `intake-job-${i}`;
                    return (
                      <li
                        key={i}
                        className="flex items-start gap-3 rounded border p-2"
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={isChecked}
                          onCheckedChange={() => toggleJobSelected(i)}
                          disabled={disabled}
                          aria-label={`Select ${j.name ?? '(unnamed job)'}`}
                        />
                        <label
                          htmlFor={checkboxId}
                          className="flex-1 cursor-pointer text-sm"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">
                              {hasName ? j.name : <em>(unnamed)</em>}
                            </span>
                            {j.skill_level && (
                              <Badge variant="outline">{j.skill_level}</Badge>
                            )}
                            {j.estimated_minutes != null && (
                              <Badge variant="outline">{j.estimated_minutes} min</Badge>
                            )}
                            {j.category && (
                              <Badge variant="secondary">{j.category}</Badge>
                            )}
                            {!hasName && (
                              <Badge variant="destructive">no name — can't attach</Badge>
                            )}
                          </div>
                          {j.rationale && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {j.rationale}
                            </div>
                          )}
                        </label>
                      </li>
                    );
                  })}
                </ul>

                <div className="flex items-center gap-3">
                  <Button
                    onClick={handleAttachJobs}
                    disabled={
                      attaching ||
                      selectedJobIndices.size === 0 ||
                      !!attachResult
                    }
                  >
                    {attaching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Attach Selected Jobs
                    {selectedJobIndices.size > 0 && !attachResult && (
                      <span className="ml-1">({selectedJobIndices.size})</span>
                    )}
                  </Button>
                  {attachError && !attaching && (
                    <Button variant="outline" onClick={handleAttachJobs}>
                      Retry
                    </Button>
                  )}
                </div>

                {attachError && (
                  <Alert variant="destructive">
                    <AlertTitle>Attach failed</AlertTitle>
                    <AlertDescription>{attachError}</AlertDescription>
                  </Alert>
                )}

                {attachResult && (
                  <Alert>
                    <AlertTitle>
                      {attachResult.already_attached
                        ? 'Jobs already attached to this ticket'
                        : `Attached ${attachResult.attached_count} job${attachResult.attached_count === 1 ? '' : 's'}`}
                    </AlertTitle>
                    <AlertDescription>
                      {attachResult.already_attached ? (
                        <p>
                          A prior call already attached jobs for this intake draft
                          (idempotent short-circuit; nothing changed).
                        </p>
                      ) : (
                        <>
                          {attachResult.attached_job_ids && attachResult.attached_job_ids.length > 0 && (
                            <p className="text-xs">
                              Attached job ids:{' '}
                              <code>{attachResult.attached_job_ids.join(', ')}</code>
                            </p>
                          )}
                          {attachResult.skipped && attachResult.skipped.length > 0 && (
                            <div className="mt-2">
                              <p className="font-medium">
                                Skipped ({attachResult.skipped.length}):
                              </p>
                              <ul className="list-disc pl-4 text-xs">
                                {attachResult.skipped.map((s, idx) => (
                                  <li key={idx}>
                                    <span className="font-medium">{s.name}</span>
                                    {' — '}
                                    <code>{s.reason}</code>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* ---------- Step 4C: OptiAssign handoff ---------- */}
          <Card>
            <CardHeader>
              <CardTitle>Assign to technician</CardTitle>
              <CardDescription>
                Hand off to OptiAssign to pick a technician + bay. Attach jobs
                first if you want the ranked placement to reflect them.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 flex-wrap">
                <Button onClick={() => setIsOptiPlaceOpen(true)}>
                  Assign with OptiAssign
                </Button>
                <Button variant="outline" onClick={handleGoToOptiboard}>
                  Go to OptiBoard
                </Button>
                <Button variant="outline" onClick={resetForAnotherIntake}>
                  Start Another Intake
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/*
        OptiPlaceModal — reassign mode (existing-ticket path, see
        OptiPlaceModal.tsx:1515 `isReassignment = !!ticketData.id`).
        Mounted at the page level so it can be opened from the done
        step. Lazy: only render when actually open AND we have the
        approve result, so we can guarantee ticketData.id is a real
        string and never undefined.
      */}
      {isOptiPlaceOpen && approveResult && extraction && (
        <OptiPlaceModal
          isOpen={isOptiPlaceOpen}
          onClose={() => setIsOptiPlaceOpen(false)}
          ticketData={{
            id: approveResult.ticket_id,
            vehicleId: approveResult.vehicle_id,
            customerId: approveResult.customer_id,
            customerName: customerForm.full_name,
            customerPhone: customerForm.phone || undefined,
            vehicle: {
              year: maybeNumber(vehicleForm.year) ?? 0,
              make: vehicleForm.make,
              model: vehicleForm.model,
              vin: vehicleForm.vin || undefined,
              licensePlate: vehicleForm.license_plate || undefined,
            },
            type: extraction.intake?.is_waiter === true ? 'Waiter' : 'Drop-off',
            priority: 'Normal',
            appointmentDate: intakeForm.promised_time || '',
            isComeback: false,
            // attach handoff covers jobs separately; OptiPlace's reassign
            // path reads from the live ticket_jobs table, not this array,
            // so passing an empty list is safe.
            jobs: [],
            predictedMinutes: 0,
          }}
          onAssignmentComplete={(ticketId) => {
            setIsOptiPlaceOpen(false);
            navigate(`/?tab=dayboard&highlight=${encodeURIComponent(ticketId)}`);
          }}
        />
      )}
    </div>
  );
}
